// Simple editable list of current insured values per vehicle -- one field
// per existing vehicle row, not a full add/delete CRUD tab like
// EftposMachinesTab.tsx, since these rows already exist as vehicles.
import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Save, Car } from "lucide-react";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";

interface VehicleValueRow {
  vehicle_number: string;
  registration: string | null;
  make: string | null;
  model: string | null;
  insured_value: number | null;
  insured_value_updated_at: string | null;
  status: string | null;
}

// vehicles.make often already has the model baked into it (e.g. make
// "Toyota Prius Alpha" / model "Prius Alpha") -- a data-entry quirk from
// before this tab existed, shared with FleetManagement/TaxiCaller sync, so
// not something to silently rewrite here. Just avoid showing it twice.
const makeModelLabel = (make: string | null, model: string | null): string => {
  const m = (make || "").trim();
  const mo = (model || "").trim();
  if (!m) return mo || "--";
  if (!mo) return m;
  if (m.toLowerCase().includes(mo.toLowerCase())) return m;
  if (mo.toLowerCase().includes(m.toLowerCase())) return mo;
  return `${m} ${mo}`;
};

const InsuranceVehicleValuesTab: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) sees only their linked vehicle(s) here, read-only unless
  // explicitly given edit rights. Unrestricted (the default -- everyone
  // today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "insurance");
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;

  const [rows, setRows] = useState<VehicleValueRow[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingFor, setSavingFor] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    let query = supabase
      .from("vehicles")
      .select("vehicle_number, registration, make, model, insured_value, insured_value_updated_at, status")
      .order("vehicle_number");
    if (scopedVehicleNumbers) query = query.in("vehicle_number", scopedVehicleNumbers.length > 0 ? scopedVehicleNumbers : ["__none__"]);
    const { data, error } = await query;
    if (error) {
      toast({ title: "Error", description: "Failed to load vehicles: " + error.message, variant: "destructive" });
    } else {
      // Same "active fleet" predicate FleetManagement.tsx uses -- excludes
      // retired/sold vehicles (and the odd "CANC" placeholder row) rather
      // than tracking insured values for vehicles no longer on the policy.
      setRows((data || []).filter((v) => v.status === "active" || !v.status));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedVehicleNumbers]);

  const saveValue = async (vehicleNumber: string) => {
    if (!canEdit) return;
    const raw = edits[vehicleNumber];
    const value = raw != null && raw !== "" ? parseFloat(raw) : null;
    if (raw != null && raw !== "" && Number.isNaN(value)) {
      toast({ title: "Error", description: "Enter a valid number", variant: "destructive" });
      return;
    }
    setSavingFor(vehicleNumber);
    const { error } = await supabase
      .from("vehicles")
      .update({ insured_value: value, insured_value_updated_at: new Date().toISOString().slice(0, 10) })
      .eq("vehicle_number", vehicleNumber);
    setSavingFor(null);
    if (error) {
      toast({ title: "Error", description: "Failed to save: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: `Insured value updated for #${vehicleNumber}` });
      setEdits((e) => {
        const next = { ...e };
        delete next[vehicleNumber];
        return next;
      });
      load();
    }
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary flex items-center gap-2">
          <Car className="w-5 h-5" />
          Vehicle Insured Values
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Current insured value per vehicle, from the fleet policy schedule -- update here at each renewal. A few
          vehicles were left blank when this was first seeded because the policy schedule's registration didn't
          cleanly match a single current vehicle -- fill those in manually.
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fleet #</TableHead>
                  <TableHead>Registration</TableHead>
                  <TableHead>Make / Model</TableHead>
                  <TableHead>Insured Value ($)</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const editValue = edits[row.vehicle_number] ?? (row.insured_value != null ? String(row.insured_value) : "");
                  const dirty = edits[row.vehicle_number] != null;
                  return (
                    <TableRow key={row.vehicle_number} className={row.insured_value == null ? "bg-red-950/20" : undefined}>
                      <TableCell className="font-medium">{row.vehicle_number}</TableCell>
                      <TableCell>{row.registration || "--"}</TableCell>
                      <TableCell>{makeModelLabel(row.make, row.model)}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={editValue}
                          onChange={(e) => setEdits((prev) => ({ ...prev, [row.vehicle_number]: e.target.value }))}
                          className="w-32 dark-input"
                          placeholder="Not set"
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.insured_value_updated_at || "--"}</TableCell>
                      <TableCell>
                        {dirty && (
                          <Button size="sm" onClick={() => saveValue(row.vehicle_number)} disabled={savingFor === row.vehicle_number} className="golden-button">
                            <Save className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InsuranceVehicleValuesTab;
